import React, { useEffect, useState } from "react";
import {
  Chip, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography, makeStyles
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  content: { padding: theme.spacing(2), overflowY: "auto" },
  filters: { display: "flex", gap: 10, padding: theme.spacing(2), marginBottom: theme.spacing(2), flexWrap: "wrap" },
  filter: { minWidth: 180 },
  change: { maxWidth: 520 },
  secondary: { color: theme.palette.text.secondary, fontSize: 12 }
}));

const entityLabels = {
  attendance: "Bate-ponto", follow_up: "Retorno", ticket: "Atendimento",
  custom_field: "Campo personalizado", ticket_custom_fields: "Dados do atendimento"
};
const actionLabels = {
  punch: "Registrou ponto", adjust: "Ajustou ponto", create: "Criou",
  update: "Alterou", move: "Moveu cartão", update_details: "Alterou atendimento",
  save_values: "Atualizou campos"
};
const priorityLabels = { low: "Baixa", normal: "Normal", high: "Alta", urgent: "Urgente" };
const parse = value => { try { return value ? JSON.parse(value) : null; } catch (_) { return null; } };
const displayValue = (key, value, columns) => {
  if (value == null || value === "") return "Não definido";
  if (key === "kanbanColumnId") return columns.find(item => Number(item.id) === Number(value))?.name || `Coluna ${value}`;
  if (key === "priority") return priorityLabels[value] || value;
  if (key === "dueAt" || key === "punchedAt" || key === "completedAt") return new Date(value).toLocaleString("pt-BR");
  if (typeof value === "object") return "Dados atualizados";
  return String(value);
};
const fieldLabels = {
  kanbanColumnId: "Etapa", priority: "Prioridade", dueAt: "Prazo", status: "Status",
  punchedAt: "Horário", type: "Tipo", note: "Observação", assignedUserId: "Responsável"
};
const describe = (log, columns) => {
  const before = parse(log.beforeData);
  const after = parse(log.afterData);
  if (log.action === "move" && before && after) {
    return `Moveu de “${displayValue("kanbanColumnId", before.kanbanColumnId, columns)}” para “${displayValue("kanbanColumnId", after.kanbanColumnId, columns)}”.`;
  }
  if (before && after && !Array.isArray(before) && !Array.isArray(after)) {
    const ignored = ["updatedAt", "createdAt", "lastMessage", "unreadMessages"];
    const changes = Object.keys(after).filter(key => !ignored.includes(key) && JSON.stringify(before[key]) !== JSON.stringify(after[key]));
    if (changes.length) return changes.map(key => `${fieldLabels[key] || key}: ${displayValue(key, before[key], columns)} → ${displayValue(key, after[key], columns)}`).join("; ");
  }
  if (log.action === "save_values") return "Atualizou os campos personalizados do atendimento.";
  if (log.action === "punch") return `Registrou ${displayValue("type", after?.type, columns)} às ${displayValue("punchedAt", after?.punchedAt, columns)}.`;
  return `${actionLabels[log.action] || log.action} ${entityLabels[log.entity]?.toLowerCase() || log.entity}.`;
};

const AuditLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [columns, setColumns] = useState([]);
  const [userId, setUserId] = useState("");
  const [entity, setEntity] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = async () => {
    try { setLogs((await api.get("/audit-logs", { params: { userId: userId || undefined, entity: entity || undefined, from: from || undefined, to: to || undefined } })).data); }
    catch (err) { toastError(err); }
  };
  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/kanban/columns")]).then(([usersResult, columnsResult]) => {
      setUsers(usersResult.data.users); setColumns(columnsResult.data);
    }).catch(toastError);
  }, []);
  useEffect(() => { const timer = setTimeout(load, 250); return () => clearTimeout(timer); }, [userId, entity, from, to]);
  return <MainContainer><MainHeader><Title>Auditoria</Title></MainHeader><div className={classes.content}>
    <Paper variant="outlined" className={classes.filters}>
      <FormControl size="small" variant="outlined" className={classes.filter}><InputLabel>Usuário</InputLabel><Select value={userId} onChange={e => setUserId(e.target.value)} label="Usuário"><MenuItem value="">Todos</MenuItem>{users.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</Select></FormControl>
      <FormControl size="small" variant="outlined" className={classes.filter}><InputLabel>Módulo</InputLabel><Select value={entity} onChange={e => setEntity(e.target.value)} label="Módulo"><MenuItem value="">Todos</MenuItem>{Object.entries(entityLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
      <TextField size="small" variant="outlined" type="date" label="De" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
      <TextField size="small" variant="outlined" type="date" label="Até" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
    </Paper>
    <Paper variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Data</TableCell><TableCell>Usuário</TableCell><TableCell>Atividade</TableCell><TableCell>Alteração realizada</TableCell><TableCell>Origem</TableCell></TableRow></TableHead><TableBody>
      {logs.map(log => <TableRow key={log.id}><TableCell>{new Date(log.createdAt).toLocaleString("pt-BR")}</TableCell><TableCell>{log.user?.name || "Sistema"}</TableCell><TableCell><Chip size="small" label={actionLabels[log.action] || log.action} /><Typography className={classes.secondary}>{entityLabels[log.entity] || log.entity} #{log.entityId}</Typography></TableCell><TableCell className={classes.change}>{describe(log, columns)}</TableCell><TableCell>{String(log.ipAddress || "").includes("172.") ? "Servidor" : log.ipAddress || "Sistema"}</TableCell></TableRow>)}
    </TableBody></Table></Paper>
  </div></MainContainer>;
};
export default AuditLogs;
